import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import './LandingPage.css';
import PeopleCard from './components/PeopleCard';
import JoinCard from './components/JoinCard';
import { FILTER_OPTIONS } from './data/mockPeople';
import Navbar from '../components/Navbar';
import { getAllTrainers } from '../api/TrainerAPI';

const NewroConnectLanding = () => {
    const [activeFilters, setActiveFilters] = useState([]);
    const navigate = useNavigate();
    const [trainers, setTrainers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [authReady, setAuthReady] = useState(false);
    const [currentUserId, setCurrentUserId] = useState(null);
    const hasFetched = useRef(false);

    // Listen for auth state changes
    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUserId(user?.uid || null);
            setAuthReady(true);
        });
        return () => unsubscribe();
    }, []);

    // Fetch trainers once auth state is ready
    useEffect(() => {
        if (!authReady || hasFetched.current) return;
        window.scrollTo(0, 0);
        hasFetched.current = true;
        initTrainers(currentUserId);
    }, [authReady, currentUserId]);

    async function initTrainers(userId) {
        setIsLoading(true);
        const res = await getAllTrainers(userId);
        if (res.success) {
            setTrainers(res.users);
        }
        setIsLoading(false);
    }

    const toggleFilter = (filter) => {
        setActiveFilters(prev =>
            prev.includes(filter)
                ? prev.filter(f => f !== filter)
                : [...prev, filter]
        );
    };

    const filteredPeople = useMemo(() => {
        if (activeFilters.length === 0) return [...trainers];
        return trainers.filter(person =>
            person.tags.some(tag => activeFilters.includes(tag))
        );
    }, [activeFilters, trainers]);

    const handlePersonClick = (person) => {
        navigate(`/connect/profile/${person.id}`);
    };

    return (
        <div className="nc-landing-page">
            <Navbar pageOn={"about"} />

            <div className="nc-landing-container">
                <header className="nc-landing-header">
                    <h1 className="nc-landing-title">
                        Connect with a Movement Expert
                    </h1>
                    <p className="nc-landing-subtitle">
                        Find professionals who can help you move better, feel stronger, and live healthier.
                    </p>

                </header>

                <div className="nc-filter-bar">
                    <div className="nc-filter-container">
                        {FILTER_OPTIONS.map(filter => (
                            <button
                                key={filter}
                                className={`nc-filter-chip ${activeFilters.includes(filter) ? 'nc-filter-chip--active' : ''}`}
                                onClick={() => toggleFilter(filter)}
                            >
                                {filter}
                            </button>
                        ))}
                    </div>
                    <button
                        className={`nc-clear-filters ${activeFilters.length > 0 ? 'nc-clear-filters--visible' : ''}`}
                        onClick={() => setActiveFilters([])}
                        disabled={activeFilters.length === 0}
                    >
                        Clear all
                    </button>
                </div>

                <div className="nc-people-grid">
                    {isLoading ? (
                        <div className="nc-grid-loader">
                            <div className="nc-loader-spinner" />
                        </div>
                    ) : (
                        <>
                            {filteredPeople.map((person, index) => (
                                <PeopleCard
                                    key={person.id}
                                    person={person}
                                    onClick={() => handlePersonClick(person)}
                                    style={{ animationDelay: `${index * 0.05}s` }}
                                />
                            ))}
                            <JoinCard
                                style={{ animationDelay: `${filteredPeople.length * 0.05}s` }}
                            />
                        </>
                    )}
                </div>

                {filteredPeople.length === 0 && (
                    <div className="nc-no-results">
                        <p>No people match your current filters.</p>
                        <button
                            className="nc-clear-filters"
                            onClick={() => setActiveFilters([])}
                        >
                            Clear filters
                        </button>
                    </div>
                )}
                {/* <JoinNewroCTA /> */}

                <footer className="nc-landing-footer">
                    <p className="nc-footer-text">
                        Your next meaningful connection is just a click away
                    </p>
                </footer>
            </div>
        </div>
    );
};

export default NewroConnectLanding;
